/*
 * <buy-box> — the one writer for the PDP call to action.
 *
 * Eight states:
 *   select      no size chosen — disabled "Select your size"
 *   available   "Add to cart"
 *   preorder    "Pre-Order" — still takes money, keeps the buy fill
 *   soldout     buttons hidden, brown "Notify me" swaps in <email-capture>
 *   restricted  sold out here only (stock exists elsewhere) — no notify path,
 *               deliberately indistinguishable from soldout to the shopper
 *   alternate   buy slot becomes a handoff panel linking to the sibling's matching size
 *   comingsoon  buy slot is the server-rendered launch sign-up, not this element's
 *   instore     buy slot is the server-rendered exclusivity panel, the same way
 */

if (!customElements.get('buy-box')) {
  const BUY_BOX_STATES = {
    select: { hidesButtons: false, offersNotify: false, buyable: false, linksOut: false, label: 'select' },
    available: { hidesButtons: false, offersNotify: false, buyable: true, linksOut: false, label: 'add' },
    preorder: { hidesButtons: false, offersNotify: false, buyable: true, linksOut: false, label: 'preorder' },
    soldout: { hidesButtons: true, offersNotify: true, buyable: false, linksOut: false, label: 'soldout' },
    restricted: { hidesButtons: false, offersNotify: false, buyable: false, linksOut: false, label: 'soldout' },
    alternate: { hidesButtons: true, offersNotify: false, buyable: false, linksOut: true, label: 'soldout' },
    /* Every slot is false because none of them is rendered: the buy row, the
       notify trigger, the alternate link and the capture are all absent before
       launch, and the launch form the server put in their place is not this
       element's to operate. The label is what the state's own CTA reads, for
       the one path that could ask — the sticky bar's mirror. */
    comingsoon: { hidesButtons: true, offersNotify: false, buyable: false, linksOut: false, label: 'notify' },
    instore: { hidesButtons: true, offersNotify: false, buyable: false, linksOut: false, label: 'soldout' },
  };

  /* Labels render onto the element's dataset, not only window.variantStrings:
     that global is assigned by a script Cookiebot's auto-blocking scan can
     defer past this element's upgrade, but the dataset can't be late. */
  const LABELS = {
    select: { attr: 'labelSelect', string: 'selectSize' },
    add: { attr: 'labelAdd', string: 'addToCart' },
    preorder: { attr: 'labelPreorder', string: 'preOrder' },
    soldout: { attr: 'labelSoldout', string: 'soldOut' },
    notify: { attr: 'labelNotify', string: 'notify' },
    pending: { attr: 'labelPending', string: 'addToCartPending' },
  };

  /* An unrecognised state fails closed onto the row that takes no money and
     offers nothing: a disabled "Sold out" over a visible button row. */
  const FALLBACK_STATE = 'restricted';

  class BuyBox extends HTMLElement {
    connectedCallback() {
      this.buttons = this.querySelector('.product-form__buttons');
      this.submit = this.querySelector('[name="add"]');
      this.submitLabel = this.submit ? this.submit.querySelector('span') : null;
      this.trigger = this.querySelector('[data-buy-box-notify-trigger]');
      this.captureSlot = this.querySelector('[data-buy-box-capture]');
      this.capture = this.captureSlot ? this.captureSlot.querySelector('email-capture') : null;
      this.captureHeading = this.querySelector('[data-buy-box-capture-heading]');
      this.handoff = this.querySelector('[data-buy-box-handoff]');
      this.handoffHeading = this.querySelector('[data-buy-box-handoff-heading]');
      this.handoffDelivery = Array.from(this.querySelectorAll('[data-handoff-delivery]'));
      this.alternateLink = this.querySelector('[data-buy-box-alternate]');

      /* The size-less wording is already resolved and translated on the element;
         keeping it is cheaper than a second locale round trip, and it is the
         honest fallback whenever no size chip can be read. */
      this.defaultSuccessMessage = this.capture ? this.capture.dataset.successMessage : '';

      /* Same reason, for the line shown when marketing consent is denied. It is
         baked at render in the mode the markup opened in, so the restock wording
         is the default and the launch wording arrives as an attribute here. */
      this.defaultConsentMessage = this.capture ? this.capture.dataset.consentMessage : '';

      /* ",4711:available,4712:restricted," -> Map { '4711' => 'available', … }.
         Every variant of the product is in it, including the discontinued sizes
         the variant JSON drops, so a ?variant= deep link always resolves. */
      this.variantStates = new Map(
        (this.dataset.variantStates || '')
          .split(',')
          .filter(Boolean)
          .map((entry) => entry.split(':'))
      );

      /* The other generation of this shoe, when the product carries a pointer to
         one. Same ,id:id, shape as the state map above, so the same parse.
         Empty on the ~95% of products with no sibling, which is what makes
         every branch below collapse to today's behaviour there. */
      this.altRole = this.dataset.altRole || '';
      this.altUrl = this.dataset.altUrl || '';
      this.altName = this.dataset.altName || '';
      this.altHandle = this.dataset.altHandle || '';
      this.altComingSoon = this.hasAttribute('data-alt-coming-soon');
      this.altMap = new Map(
        (this.dataset.altMap || '')
          .split(',')
          .filter(Boolean)
          .map((entry) => entry.split(':'))
      );
      /* Keyed by the OTHER generation's variant id, the value altMap yields. */
      const idSet = (value) => new Set((value || '').split(',').filter(Boolean));
      this.altPreorderIds = idSet(this.handoff?.dataset.preorderIds);
      this.altFactoryIds = idSet(this.handoff?.dataset.factoryIds);

      this.state = this.dataset.state || 'select';
      this.variantId = this.dataset.variantId || '';
      this.pending = false;
      this.captureOpen = false;

      this.onTriggerClick = this.showCapture.bind(this);
      if (this.trigger) this.trigger.addEventListener('click', this.onTriggerClick);

      /* Also the repair for product-form.js's finally block, which clears
         aria-disabled unconditionally after an add — see the note there. */
      if (this.submit) {
        this.submitObserver = new MutationObserver(this.handleSubmitClass.bind(this));
        this.submitObserver.observe(this.submit, { attributes: true, attributeFilter: ['class'] });
      }

      /* Form restoration can also land after this element has connected, so the
         re-read repeats once the page is fully shown. */
      this.onPageShow = this.resyncFromControls.bind(this);
      window.addEventListener('pageshow', this.onPageShow);

      /* Before applyState(), so a stale snapshot is never applied over a live
         choice — not even for the frame it would take to correct it. */
      this.resyncFromControls();

      /* The markup ships today's two-state button; the sold-out layout is applied
         here instead, so blocking this file leaves the shopper with a working
         "Sold out" rather than a brown button that opens nothing. */
      this.applyState();
    }

    disconnectedCallback() {
      if (this.trigger) this.trigger.removeEventListener('click', this.onTriggerClick);
      if (this.submitObserver) this.submitObserver.disconnect();
      window.removeEventListener('pageshow', this.onPageShow);
    }

    /* data-state describes the page as the server rendered it, and two things can
       invalidate it before this element upgrades: a slow script, where the shopper
       reaches the native select first, and a back navigation or reload, where the
       browser restores the select's value without firing `change`. Both leave the
       select pointing at a size the snapshot never saw, and applying the snapshot
       over it disables a choice that is visibly selected.
       Nothing is resolved here: <variant-selects> owns which variant is selected
       and the hidden id input that carries it, so deciding the state locally would
       light the CTA over a form that still carries no variant. */
    resyncFromControls() {
      const variantSelects = document.querySelector(
        `variant-selects[data-section="${this.dataset.section}"]`
      );
      /* Presence is not upgrade readiness: with global.js blocked nothing would
         consume the event, and the server's state is the honest fallback. */
      if (!variantSelects || typeof variantSelects.onVariantChange !== 'function') return;

      const drifted = Array.from(variantSelects.querySelectorAll('select')).find((select) => {
        /* What the select would read with no restoration and no shopper: the
           option Liquid marked selected, or the browser's default of the first. */
        const rendered = select.querySelector('option[selected]') || select.options[0];
        return rendered && select.value !== rendered.value;
      });
      if (!drifted) return;

      /* Replaying the event the restoration never fired, rather than calling
         <variant-selects> directly: it is the same dispatch every other writer
         uses (the chips, Ecomid), so <size-picker> re-checks its chip off it too.
         One is enough for several drifted selects — updateOptions() reads them
         all — and it resolves this element's state through setState(). */
      drifted.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /* Called synchronously from VariantSelects.onVariantChange, before any fetch.
       `state` is the coarse answer <variant-selects> can give from the variant
       JSON: 'select', 'available' or 'soldout'. Only 'select' is used — it is the
       one state that is not a property of any variant, because nothing is chosen
       yet. Everything else comes from the map, which already knows about
       pre-order and restricted; the coarse answer is kept only as the fallback
       for the no-variant case, where the chosen options match nothing at all. */
    setState(state, variant) {
      this.variantId = variant && variant.id ? String(variant.id) : '';
      this.state = state === 'select' ? 'select' : this.variantStates.get(this.variantId) || state;
      this.dataset.state = this.state;
      this.applyState();
    }

    stateRow() {
      return BUY_BOX_STATES[this.state] || BUY_BOX_STATES[FALLBACK_STATE];
    }

    /* For Ecomid's size finder, which closes its overlay over a size it just
       wrote into the select and has only the variant JSON otherwise — where
       `alternate`, `restricted` and plain sold-out are all `available: false`. */
    nextAction() {
      return { ...this.stateRow() };
    }

    applyState() {
      const row = this.stateRow();

      if (this.buttons) this.buttons.hidden = row.hidesButtons;

      /* Bound on every entry into a notify state, not only when the capture is
         shown: a shopper who picks a sold-out size and types fast must not
         subscribe to the size they were looking at a moment ago. */
      if (row.offersNotify) this.bindCapture();
      else this.closeCapture();

      if (this.trigger) this.trigger.hidden = !row.offersNotify || this.captureOpen;

      if (this.handoff) {
        if (row.linksOut) this.fillHandoff();
        this.handoff.hidden = !row.linksOut;
      }

      this.applyButton();
    }

    /* Everything in the panel that depends on the size: the link, and which
       headline and delivery line are true for the matching variant over there. */
    fillHandoff() {
      const altId = this.altMap.get(this.variantId) || '';
      let kind = 'stock';
      if (this.altPreorderIds.has(altId)) kind = 'preorder';
      else if (this.altFactoryIds.has(altId)) kind = 'factory';

      this.handoffDelivery.forEach((line) => {
        line.hidden = line.dataset.handoffDelivery !== kind;
      });

      const data = this.handoff.dataset;
      const headings = { stock: data.headingStock, preorder: data.headingPreorder, factory: data.headingFactory };
      const heading = headings[kind] || '';
      if (this.handoffHeading) {
        this.handoffHeading.textContent = heading;
        this.handoffHeading.hidden = heading === '';
      }

      if (this.alternateLink) this.alternateLink.href = this.altHref();
    }

    /* Falls back to the bare product URL rather than `undefined`: the map is
       rendered from the same pass that decides these states, so a miss means
       the two disagree, and the product page is still right while the size isn't. */
    altHref() {
      const altId = this.altMap.get(this.variantId);
      return altId ? `${this.altUrl}?variant=${altId}` : this.altUrl;
    }

    labelFor(name) {
      const source = LABELS[name];
      if (!source) return '';
      return this.dataset[source.attr] || (window.variantStrings || {})[source.string] || '';
    }

    applyButton() {
      if (!this.submit || !this.submitLabel) return;

      /* Mid-submit the label belongs to the pending copy and the disabled state
         belongs to product-form.js. Touching either here would fight it; the
         state re-applies itself when the loading class comes back off. */
      if (this.pending) {
        const pending = this.labelFor('pending');
        if (pending) this.submitLabel.textContent = pending;
        return;
      }

      const row = this.stateRow();

      if (row.buyable) {
        this.submit.removeAttribute('disabled');
        this.submit.removeAttribute('aria-disabled');
      } else {
        this.submit.setAttribute('disabled', 'disabled');
        this.submit.setAttribute('aria-disabled', 'true');
      }

      const label = this.labelFor(row.label);
      if (label) this.submitLabel.textContent = label;
    }

    handleSubmitClass() {
      const pending = this.submit.classList.contains('loading');
      if (pending === this.pending) return;
      this.pending = pending;
      this.applyButton();
    }

    showCapture() {
      /* Ecomid calls this synchronously from its own out-of-stock path, so the
         state it arrives in is not the trigger's. Opening the capture anywhere
         but soldout would offer a restock alert for a size on sale right now. */
      if (this.state !== 'soldout' || !this.captureSlot) return;

      /* Re-bound rather than relying on applyState()'s bind: <variant-selects>
         resolves the state before <size-picker> re-checks the chips, so a
         select-driven pick (Ecomid, the native control) binds the message while
         the PREVIOUS chip is still the checked one. Every change listener has
         run by the time a caller reaches here, so this reads the right size. */
      this.bindCapture();

      this.captureOpen = true;
      if (this.trigger) this.trigger.hidden = true;
      this.captureSlot.hidden = false;
      const input = this.captureSlot.querySelector('input[type="email"]');
      if (!input) return;

      /* preventScroll stops WebKit's native focus-scroll, which otherwise wins
         over any scrollIntoView issued after it. Two rAFs, not one: called in
         the same tick as `hidden = false`, WebKit still computes the target
         from pre-reveal layout and freezes the scroll ~334px short — measured. */
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          input.focus({ preventScroll: true });
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          input.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
        });
      });
    }

    closeCapture() {
      this.captureOpen = false;
      if (this.captureSlot) this.captureSlot.hidden = true;
    }

    /* Writes the whole resolved binding before the shopper can reach the field.
       <email-capture> observes these attributes and coalesces the writes into a
       single render, so this also resets any success or error state left over
       from the previous size — once, not once per attribute. */
    bindCapture() {
      if (!this.capture) return;

      const capture = this.resolveCapture();
      const data = this.capture.dataset;

      /* Written on every bind, not only on the flip into launch mode: the next
         size can be an ordinary restock, and a line naming the successor left
         standing over it would promise a launch that size is not on. */
      if (this.captureHeading) {
        const heading = capture.mode === 'launch' ? this.dataset.launchHeading : '';
        this.captureHeading.textContent = heading;
        this.captureHeading.hidden = heading === '';
      }

      if (capture.successMessage) data.successMessage = capture.successMessage;
      data.consentMessage = capture.consentMessage;

      /* Launch mode keys on the handle, not the variant, so the handle has to be
         in place before the mode flips. */
      if (capture.mode === 'launch') data.productHandle = capture.handle;
      data.mode = capture.mode;

      /* The plain dataset write is the binding, not a method on the element:
         quick-add re-injects this whole section with its scripts re-created
         async, so <email-capture> may not have upgraded yet when this runs. It
         observes data-variant-id and picks the value up on upgrade. */
      data.variantId = capture.variantId;
    }

    /* One answer for what this capture is, since the Klaviyo destination, bound
       target, confirmation copy and consent-denied line all have to agree.
       Gated on the re-point, not the flag alone: no re-point means no launch
       was promised, so the sign-up stays on this page's own variant. */
    resolveCapture() {
      const variantId = this.captureVariantId();
      const usingAlt = variantId !== this.variantId;
      const mode = this.altComingSoon && usingAlt ? 'launch' : 'restock';

      return {
        mode,
        variantId,
        handle: this.altHandle,
        successMessage: this.successMessage(mode, usingAlt),
        consentMessage:
          mode === 'launch'
            ? this.dataset.consentLaunch || this.defaultConsentMessage
            : this.defaultConsentMessage,
      };
    }

    /* Reached only in `soldout`: a successor carrying this size would have made
       the state `alternate` instead, so sold-out-here means sold-out-on-both —
       the successor's variant is the one that will actually restock. */
    captureVariantId() {
      if (this.altRole !== 'successor') return this.variantId;
      return this.altMap.get(this.variantId) || this.variantId;
    }

    successMessage(mode, usingAlt) {
      if (mode === 'launch') return this.dataset.successLaunch || this.defaultSuccessMessage;

      const size = this.selectedSizeLabel();
      /* usingAlt names the sibling product in the confirmation, since the email
         shows a product the shopper never looked at otherwise. */
      if (usingAlt) {
        const altDefault = this.dataset.successAlt || this.defaultSuccessMessage;
        const template = this.dataset.successTemplateAlt;
        if (!size || !template) return altDefault;
        return template.replace('[size]', size).replace('[name]', this.altName);
      }

      const template = this.dataset.successTemplate;
      if (!size || !template) return this.defaultSuccessMessage;
      return template.replace('[size]', size);
    }

    /* The chip's label for the system the shopper is actually reading — never the
       raw variant title, which is the whole cross-system string
       ("UK 4 / EU 38 / US 4.5"). No chip (single-size run, a non-size option)
       means no size to name, and the size-less message stands. */
    selectedSizeLabel() {
      const section = this.closest('section');
      if (!section) return '';

      const picker = section.querySelector('[data-size-picker]');
      if (!picker) return '';

      /* By the select's value, not :checked: on a select-driven pick (Ecomid,
         form restoration) <variant-selects> resolves the state before
         <size-picker> re-checks the chips, so :checked is still the last size. */
      const select = picker.closest('size-picker')?.querySelector('.product-form__input--dropdown select');
      const radios = Array.from(picker.querySelectorAll('input[type="radio"]'));
      const picked = select
        ? radios.find((radio) => radio.value === select.value)
        : radios.find((radio) => radio.checked);
      const chip = picked ? picked.closest('.size-picker__chip') : null;
      if (!chip) return '';

      // data-size-system only ever holds UK|EU|US|cm|in, so no escaping is needed.
      const system = picker.dataset.sizeSystem;
      const label =
        (system && chip.querySelector(`.size-picker__label[data-system="${system}"]`)) ||
        chip.querySelector('.size-picker__label');

      return label ? label.textContent.trim() : '';
    }
  }

  customElements.define('buy-box', BuyBox);
}
